import { useEffect } from "react";
import { z } from "zod";

import { WarningCircle as WarningCircleIcon } from "@phosphor-icons/react";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { AuthApiFactory } from "@/apis/auth";
import { useSubscriptionStore } from "@/store/subscription";
import { useInitializeSession } from "@/hooks/use-initialize";
import { useAuthenticationStore } from "@/store/authentication";

export const Route = createFileRoute("/external/auth")({
  validateSearch: z.object({
    code: z.string().nullish(),
    next: z.string().nullish(),
  }),
  component: RouteComponent,
});

function RouteComponent() {
  const params = Route.useSearch();
  const navigate = Route.useNavigate();

  const initializeSession = useInitializeSession();
  const authenticate = useAuthenticationStore((store) => store.authenticate);
  const setSubscription = useSubscriptionStore((store) => store.setSubscription);

  const {
    mutate: verifyMagicLink,
    status,
    error,
  } = useMutation({
    mutationFn: AuthApiFactory.Apis.VerifyMagicLink,
    onSuccess: async () => {
      const session = await AuthApiFactory.Apis.Authenticate();
      const initialize = await initializeSession();
      authenticate(session.data.user, session.data.organization, initialize.profile);
      setSubscription(initialize.subscription);
      navigate({ to: params.next || "/dashboard" });
    },
    onError: () => {
      setTimeout(() => navigate({ to: "/auth", search: { next: params.next } }), 3000);
    },
  });

  useEffect(() => {
    if (params.code) {
      verifyMagicLink(params.code);
      return;
    }

    setTimeout(() => navigate({ to: "/auth", search: { next: params.next } }), 3000);
  }, [params.code, params.next, verifyMagicLink, navigate]);

  if (!params.code) {
    return (
      <div className="flex flex-col items-center justify-center h-[100dvh] w-screen text-center">
        <WarningCircleIcon className="size-8 text-destructive" />
        <p className="text-base font-medium text-foreground mt-3">Failed to login</p>
        <p className="text-sm text-muted-foreground mt-1 max-w-96">We received an invalid code. You will be redirected back in 3 seconds...</p>
      </div>
    );
  }

  switch (status) {
    case "error":
      return (
        <div className="flex flex-col items-center justify-center h-[100dvh] w-screen text-center">
          <WarningCircleIcon className="size-8 text-destructive" />
          <p className="text-base font-medium text-foreground mt-3">Failed to login</p>
          <p className="text-sm text-muted-foreground mt-1 max-w-96">{error.message}. You will be redirected back in 3 seconds...</p>
        </div>
      );

    default:
      return (
        <div className="flex items-center justify-center gap-3 h-[100dvh] w-screen">
          <div className="spinner size-6 !border-foreground" />
        </div>
      );
  }
}
